/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import outputConsole from '@ahd/shared/tools/output-console';
import axios, { type AxiosError, type AxiosResponse } from 'axios';

import { DebugLevel } from '../../_configs/types';
import { DBLogLevel, DBLogSource } from '../types/log';
import { createLog } from './createLog';

declare module 'axios' {
    interface AxiosRequestConfig {
        debug: DebugLevel;
        idGroup: number | undefined;
        noLogging?: boolean;
        title?: string;
    }
}

const axiosInstance = axios.create({
    debug: DebugLevel.NONE,
    idGroup: undefined,
    timeout: 7500,
});

axiosInstance.interceptors.response.use(
    function (serviceResponse: AxiosResponse): Promise<AxiosResponse> {
        if (serviceResponse.data instanceof Blob) {
            const contentDisposition = serviceResponse.headers['content-disposition'] || '';
            const filename = contentDisposition.replace('attachment; filename="', '').replace('"', '');
            const file = new File([serviceResponse.data], filename, { type: serviceResponse.headers['content-type'] });
            return Promise.resolve({ ...serviceResponse, data: URL.createObjectURL(file), file: file });
        } else {
            return Promise.resolve(serviceResponse);
        }
    },
    async function (serviceError: AxiosError): Promise<AxiosError> {
        if (serviceError.code === 'ERR_CANCELED') return Promise.reject({ aborted: true, message: 'Request aborted' });

        const newServiceError = Object.assign({}, serviceError);
        if (newServiceError?.code === 'ERR_CANCELED') {
            newServiceError.message = 'Die Anfrage wurde abgebrochen!';
        }
        if (newServiceError?.code === 'ECONNABORTED') {
            newServiceError.message = 'Die Anfrage hat zu lange gebraucht, um eine Antwort zu senden! Bitte versuchen Sie es später erneut!';
        }

        if (!isUndefinedNullOrEmpty(newServiceError?.response?.data)) {
            if (newServiceError?.response?.data instanceof Blob) {
                const blobText = await newServiceError?.response?.data?.text();
                newServiceError.response.data = blobText;
            } else if (typeof newServiceError?.response?.data === 'string') {
                newServiceError.message = newServiceError?.response.data;
            } else if (!isUndefinedNullOrEmpty((newServiceError?.response?.data as any)?.message)) {
                newServiceError.message = (newServiceError?.response?.data as any)?.message;
            } else {
                newServiceError.message = JSON.stringify(newServiceError?.response?.data);
            }
        }
        if (newServiceError?.code !== 'ERR_CANCELED') {
            outputConsole('error', 'axios', 'Error', newServiceError);
        }
        if (newServiceError?.code !== 'ERR_CANCELED' && !newServiceError?.config?.noLogging) {
            await createLog(
                {
                    idGroup: newServiceError?.config?.idGroup,
                    idProject: undefined,
                    level:
                        newServiceError?.response?.status === 204 || newServiceError?.response?.status === 401 || newServiceError?.response?.status === 403 || newServiceError?.response?.status === 404
                            ? DBLogLevel.Warning
                            : DBLogLevel.Error,
                    message: newServiceError?.message,
                    raw: {
                        code: newServiceError?.code,
                        config: {
                            data: newServiceError?.config?.data,
                            headers: newServiceError?.config?.headers,
                            method: newServiceError?.config?.method,
                            timeout: newServiceError?.config?.timeout,
                            url: newServiceError?.config?.url,
                        },
                        message: newServiceError?.message,
                        name: newServiceError?.name,
                        response: {
                            data: newServiceError?.response?.data,
                            headers: newServiceError?.response?.headers,
                            status: newServiceError?.response?.status,
                            statusText: newServiceError?.response?.statusText,
                        },
                    },
                    origin: DBLogSource.Fleet,
                    title: newServiceError?.config?.title || newServiceError?.name || newServiceError?.code,
                },
                newServiceError?.config?.debug
            );
        }

        return Promise.reject(newServiceError);
    }
);

export default axiosInstance;
